import React from "react";
import { Calendar, Twitter, Linkedin, Instagram, Facebook } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const HomepageFooter: React.FC = () => {
  const navigate = useNavigate();

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      if (window.location.pathname === "/") {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        navigate(`/${href}`);
      }
    }
  };

  return (
    <footer className="bg-[#17265a] text-white border-t border-[#0f1a3f] mt-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid gap-10 md:grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,1fr))]">
          {/* Brand / description */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white text-[#17265a]">
                <Calendar className="w-5 h-5" />
              </div>
              <span className="text-xl font-semibold tracking-tight">
                Aiserwave
              </span>
            </div>
            <p className="text-sm text-white/80 max-w-sm">
              Tools for discovering, attending, and remembering the events
              that matter—built for communities, teams, and curious humans.
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="inline-flex items-center rounded-full bg-white/5 px-3 py-1 text-[0.7rem] uppercase tracking-[0.16em] text-white/70">
                <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2" />
                Live events, worldwide
              </div>
            </div>
          </div>

          {/* Columns */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold tracking-wide text-white/90">
              Product
            </h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li>
                <a
                  href="#features"
                  onClick={(e) => handleAnchorClick(e, "#features")}
                  className="hover:text-white transition-colors"
                >
                  Features
                </a>
              </li>
              <li>
                <a
                  href="#events"
                  onClick={(e) => handleAnchorClick(e, "#events")}
                  className="hover:text-white transition-colors"
                >
                  Browse events
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/about");
                  }}
                  className="hover:text-white transition-colors"
                >
                  For organizers
                </a>
              </li>
              <li>
                <a
                  href="#faq"
                  onClick={(e) => handleAnchorClick(e, "#faq")}
                  className="hover:text-white transition-colors"
                >
                  Pricing & FAQ
                </a>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold tracking-wide text-white/90">
              Company
            </h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li>
                <a
                  href="/about"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/about");
                  }}
                  className="hover:text-white transition-colors"
                >
                  About
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Press
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Careers
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold tracking-wide text-white/90">
              Legal
            </h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Privacy
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Terms
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Cookies
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Accessibility
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-xs text-white/60">
            <span>
              © {new Date().getFullYear()} Aiserwave. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-3">
            {[
              { icon: Twitter, label: "Twitter" },
              { icon: Instagram, label: "Instagram" },
              { icon: Linkedin, label: "LinkedIn" },
              { icon: Facebook, label: "Facebook" },
            ].map(({ icon: Icon, label }) => (
              <a
                key={label}
                href="#"
                aria-label={label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

